import { contactPipelineInput } from 'app/lib/contactPipelineInput'
import { withZod } from '@remix-validated-form/with-zod'
import { compact, isEmpty } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { z } from 'zod'
import FormCombo from '../form/FormCombo'
import { SmartForm } from '../form/SmartForm'
import type { ContactJson, PipelineType } from 'app/types/entities'
import type { PipelineCatalogEntry } from 'app/components/action-center/usePipelinesCatalog'
import { useListCopy } from 'app/hooks/useListCopy'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore, usePipelineStore } from 'app/stores/StoreContext'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const pipelineSchema = withZod(
  z.object({
    id: z.string().min(1, { message: `Contact ID is required` }),
    pipelineId: z.string().min(1, { message: `Pipeline ID is required` }),
    stage: z.string().min(1, { message: `Stage is required` }),
  }),
)

export function AddPipeline({
  contact,
  onContactChange,
  onSaved,
  lists,
  onAddToList,
}: {
  contact: ContactJson
  /** Fired immediately with the optimistic shape so the caller can paint the new chip. */
  onContactChange?: (contact: ContactJson) => void
  /**
   * Fired after the write commits, with the contact the server returned. Callers that
   * react by refetching must use this: refetching off `onContactChange` races the
   * in-flight mutation and reads back the pre-add pipelines.
   */
  onSaved?: (contact: ContactJson) => void
  /** The lists offered when `onAddToList` is set; the pipeline store is not read then. */
  lists?: PipelineCatalogEntry[]
  /** With the grouped nav on, the caller owns the write and the lists offered. */
  onAddToList?: (listId: string, stageName: string) => Promise<boolean>
}): ReactElement {
  const pipelineStore = usePipelineStore()
  const contactStore = useContactStore()
  const copy = useListCopy()
  const [pipelines, setPipelines] = useState<PipelineType[]>([])
  const [pipeline, setPipeline] = useState<{ id?: string; name?: string; stage?: string }>()
  const [validationError, setValidationError] = useState<string>()

  useEffect(() => {
    if (onAddToList) return
    // assignableHits, not the raw fetch: a system pipeline (Hygiene Recall, Unscheduled
    // treatment) is written only by its own board, never picked from a generic modal
    // like this one. See `isAssignablePipeline`.
    pipelineStore.fetchList().then(() => {
      const contactPipelineIds = new Set(compact(contact.pipelines ?? []).map((p) => p.id))
      setPipelines(pipelineStore.assignableHits.filter((p) => !contactPipelineIds.has(p.id!)))
    })
  }, [])

  const offered: PipelineType[] = onAddToList
    ? (lists ?? []).map((list) => ({
        id: list.id,
        name: list.name,
        stages: [...list.stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((stage) => ({ name: stage.name })),
      }))
    : pipelines

  debug([`contacts`, `pipelines`], `AddPipeline`, `contact`, contact.pipelines)

  return (
    <SmartForm
      id="add-pipeline"
      className="mt-0! min-h-min w-full md:w-[680px] gap-y-0! px-6 pt-1.5 text-sm! font-medium! leading-5!"
      validator={pipelineSchema}
      hiddenInputs={[
        { name: `id`, value: contact.id },
        ...(pipeline
          ? [
              { name: `pipelineId`, value: pipeline.id },
              { name: `stage`, value: pipeline.stage },
            ]
          : []),
      ]}
      onSubmit={async (data) => {
        let result = await pipelineSchema.validate(data)
        if (result.error) {
          debug([`pipelines`, `contacts`], `error`, result.error)
          setValidationError(copy.selectAListAndStage)
        } else if (onAddToList) {
          closeModal()
          await onAddToList(data.pipelineId, data.stage)
        } else {
          const newPipelines = [
            ...(contact.pipelines ?? []).map((p) => ({ id: p.id, stage: p.stage })),
            { id: data.pipelineId, stage: data.stage },
          ]
          const input = contactPipelineInput(newPipelines)
          if (!input) {
            setValidationError(`An existing pipeline has no stage. Select its stage before saving.`)
            return
          }
          onContactChange?.({ ...contact, pipelines: newPipelines } as ContactJson)
          notifySuccess(copy.contactAddedToList)
          closeModal()
          const mutationResult = await contactStore.update(contact.id!, { pipelines: input })
          if (mutationResult.error) {
            onContactChange?.({ ...contact } as ContactJson)
            notifyError(copy.errorAddingContactToList, `pipeline-add-error`)
          } else {
            const saved =
              (mutationResult.data as { updateContact?: ContactJson } | undefined)?.updateContact ??
              ({ ...contact, pipelines: newPipelines } as ContactJson)
            contactStore.addPendingPipelineContact(data.pipelineId, saved)
            onSaved?.(saved)
          }
        }
      }}
      onCancel={() => closeModal()}
      preventSubmit
    >
      <FormCombo
        cols={6}
        name="pipeline"
        label={copy.Noun}
        placeholder={copy.selectAListPlaceholder}
        labelClassName="mb-1.5 block text-sm font-medium leading-4 text-strong"
        wrapperClassName="my-1.5 text-sm font-medium leading-5 text-strong"
        value={pipeline?.id ? { id: pipeline.id, name: pipeline.name!, value: pipeline.id } : undefined}
        options={offered.map((p) => ({ id: p.id!, name: p.name!, value: p.id! }))}
        onChange={(value) => setPipeline({ id: value.id!, name: value.name! })}
      />
      {/* col-span-6 so the note spans the form's 6-col grid at >=sm (a bare block would sit in one column). */}
      {copy.filterListsHelper && <p className="col-span-6 text-body-xs text-subtle">{copy.filterListsHelper}</p>}
      <FormCombo
        cols={6}
        name="stage-combo"
        label="Stage"
        disabled={isEmpty(pipeline)}
        placeholder="Select a stage"
        value={isEmpty(pipeline) ? undefined : { id: pipeline!.id!, value: pipeline!.stage, name: pipeline!.stage! }}
        labelClassName="mb-1.5 block text-sm font-medium leading-4 text-strong"
        options={
          offered
            .find((p) => p.id === pipeline?.id)
            ?.stages?.map((s) => ({ id: s.name, name: s.name, value: s.name }))
            .filter((s): s is OptionType => Boolean(s.id && s.name && s.value)) ?? []
        }
        wrapperClassName="my-1.5 text-sm font-medium leading-5 text-strong"
        onChange={(value) => setPipeline((prev) => ({ ...prev, stage: value.value! }))}
      />
      {validationError && <span className="text-sm text-red-500">{validationError}</span>}
    </SmartForm>
  )
}
